import React, { useEffect, useRef, useState } from "react";
import * as XLSX from "xlsx";
import { toast } from "react-toastify";
import SummaryApi from "../common";
import axios from "axios";
import Modal from "react-modal";

const authHeaders = { Authorization: "Bearer sandeep_university_916" };

const toList = (data) => (Array.isArray(data) ? data : data?.data || []);

// ────────────────────────────────────────────────
// THEME — same rules as other files
// ────────────────────────────────────────────────
const THEME = {
  primary: 'rgb(23, 44, 87)',        // #172C57
  primaryLight: 'rgba(23, 44, 87, 0.08)',
  actionGreen: '#10B981',
  actionGreenHover: '#059669',
  actionRed: '#EF4444',
  actionRedHover: '#DC2626',
};

// Order used to find the first missing field
// NOTE: 'universityId' removed — that field was deleted from the form
const FIELD_ORDER = [
  "region",
  "districtId",
  "description",
  "recordCount",
  "uploadedBy",
];

const Field = ({ id, label, required, error, children }) => (
  <div className="flex flex-col">
    <label
      htmlFor={id}
      className="block text-sm font-semibold mb-1"
      style={{ color: THEME.primary }}
    >
      {label}
      {required && <span className="ml-1 text-red-500">*</span>}
    </label>
    {children}
    {error && <p className="mt-1 text-xs text-red-500">{error}</p>}
  </div>
);

const initialForm = {
  region: "",
  district_id: "",
  description: "",
  uploaded_count: "",
  uploaded_by: "",
};

const ExcelUploadPage = () => {
  const [districts, setDistricts] = useState([]);
  const [region, setRegion] = useState("");
  const [districtId, setDistrictId] = useState("");
  const [description, setDescription] = useState("");
  const [recordCount, setRecordCount] = useState("");
  const [uploadedBy, setUploadedBy] = useState("");
  const [confirmOpen, setConfirmOpen] = useState(false);

  const [fileRowCount, setFileRowCount] = useState(null);
  const [file, setFile] = useState(null);
  const [fileData, setFileData] = useState(null);

  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [duplicateData, setDuplicateData] = useState([]);
  const fileInputRef = useRef(null);
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [confirmationMessage, setConfirmationMessage] = useState("");
  const [isInserted, setIsInserted] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [form, setForm] = useState(initialForm);
  const [uploading, setUploading] = useState(false);
  const [fileKey, setFileKey] = useState(0);
  const [activeTab, setActiveTab] = useState("summary");

  // ---------- per-field validation ----------
  const fieldErrors = {};
  if (!region.trim()) fieldErrors.region = "Please enter the region";
  if (!districtId) fieldErrors.districtId = "Please select a district";
  if (!description.trim()) fieldErrors.description = "Please enter a description";
  if (!(Number(recordCount) > 0))
    fieldErrors.recordCount = "Enter the number of records (greater than 0)";
  if (!uploadedBy.trim()) fieldErrors.uploadedBy = "Please enter your name";

  const detailsFilled = Object.keys(fieldErrors).length === 0;
  const fileError =
    submitted && detailsFilled && !file ? "Please choose an Excel file" : "";

  useEffect(() => {
    const loadDistricts = async () => {
      try {
        const res = await fetch(SummaryApi.GetAllDisticts.url, {
          method: SummaryApi.GetAllDisticts.method,
        });
        const list = toList(await res.json());
        setDistricts(list.filter((d) => Number(d.is_active) === 1));
      } catch (err) {
        toast.error("Failed to load districts");
      }
    };

    loadDistricts();
  }, []);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      const fileExtension = selectedFile.name.split(".").pop().toLowerCase();
      if (fileExtension !== "xlsx" && fileExtension !== "xls") {
        setError("Please upload a valid Excel file.");
        setFile(null);
        return;
      }
      setError("");
      setFile(selectedFile);
      const reader = new FileReader();
      reader.onload = (e) => {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: "array" });
        const sheetName = workbook.SheetNames[0];
        const sheet = workbook.Sheets[sheetName];
        const jsonData = XLSX.utils.sheet_to_json(sheet);
        setFileRowCount(jsonData.length);
        setFileData(jsonData);

        if (jsonData.length !== Number(recordCount)) {
          setError(
            `The file has ${jsonData.length} records but you entered ${recordCount}.`,
          );
        }
      };
      reader.readAsArrayBuffer(selectedFile);
    }
  };

  const handleModalOpen = (message) => {
    setConfirmationMessage(message);
    setModalIsOpen(true);
  };

  const handleProceed = async () => {
    setModalIsOpen(false);
    insertData();
  };

  const handleCancel = () => {
    setModalIsOpen(false);
  };

  const resetForm = () => {
    setSubmitted(false);
    setRegion("");
    setDistrictId("");
    setDescription("");
    setRecordCount("");
    setUploadedBy("");
    setFile(null);
    setFileData(null);
    setFileRowCount(null);
    setDuplicateData([]);
    setError("");
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const downloadDuplicates = () => {
    const ws = XLSX.utils.json_to_sheet(uploadResult.duplicates);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Duplicates");
    XLSX.writeFile(wb, "duplicate_records.xlsx");
  };

  const startUpload = async () => {
    if (!detailsFilled) {
      setError("Please fill all the required fields before uploading.");
      return;
    }

    if (!file) {
      setError("Please upload an Excel file.");
      return;
    }

    if (fileRowCount !== Number(recordCount)) {
      setError(
        `The file has ${fileRowCount} records but you entered ${recordCount}.`,
      );
      return;
    }

    setError("");
    setLoading(true);
    const formData = new FormData();
    formData.append("excel_file", file);

    try {
      const response = await fetch(SummaryApi.verificationDuplicatesData.url, {
        method: SummaryApi.verificationDuplicatesData.method,
        headers: authHeaders,
        body: formData,
      });

      const data = await response.json();
      if (data.status) {
        setDuplicateData(data.duplicates.data);
        if (data.duplicates.count > 0) {
          const newWindow = window.open("", "_blank");
          newWindow.document.write(
            "<html><head><title>Duplicate Data</title></head><body>",
          );

          newWindow.document.write(
            '<link href="https://cdn.jsdelivr.net/npm/tailwindcss@2.2.19/dist/tailwind.min.css" rel="stylesheet">',
          );
          newWindow.document.write(
            '<link rel="stylesheet" href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.0.0-beta3/css/all.min.css" />',
          );

          newWindow.document.write(`
            <div class="p-5 relative">
              <div class="absolute top-5 right-5 bg-blue-500 text-white rounded-full px-4 py-2 text-sm font-bold shadow-lg">
                Total Duplicates: ${data.duplicates.count}
              </div>

              <div class="mb-5">
                <button
                  class="flex items-center bg-green-500 hover:bg-green-600 text-white font-bold py-3 px-6 rounded-lg shadow-lg text-lg"
                  onclick="handleDownloadExcel()"
                >
                  <i class="fas fa-download mr-2"></i> Download Excel
                </button>
              </div>

              <h1 class="text-2xl font-bold mb-4 text-center">Duplicate Data</h1>
              <h1 class="text-xl font-bold mb-4 text-center">Mobile Number already existed</h1>
              <table class="table-auto w-full border border-collapse border-gray-200">
                <thead>
                  <tr class="bg-gray-100">
                    <th class="border border-gray-300 px-4 py-2 text-center">SNO</th>
                    <th class="border border-gray-300 px-4 py-2 text-center">Mobile Number</th>
                    <th class="border border-gray-300 px-4 py-2 text-center">Student Name</th>
                    <th class="border border-gray-300 px-4 py-2 text-center">College</th>
                    <th class="border border-gray-300 px-4 py-2 text-center">Course</th>
                    <th class="border border-gray-300 px-4 py-2 text-center">Year</th>
                  </tr>
                </thead>
                <tbody>
          `);

          data.duplicates.data.forEach((row, index) => {
            newWindow.document.write(`
              <tr>
                <td class="border border-gray-300 px-4 py-2 text-center">${index + 1}</td>
                <td class="border border-gray-300 px-4 py-2 text-center">${row.mobile_number}</td>
                <td class="border border-gray-300 px-4 py-2 text-center">${row.student_name}</td>
                <td class="border border-gray-300 px-4 py-2 text-center">${row.clg_name}</td>
                <td class="border border-gray-300 px-4 py-2 text-center">${row.course}</td>
                <td class="border border-gray-300 px-4 py-2 text-center">${row.year}</td>
              </tr>
            `);
          });

          newWindow.document.write(`
                </tbody>
              </table>
            </div>
          `);
          newWindow.document.write(`
            <script src="https://cdnjs.cloudflare.com/ajax/libs/xlsx/0.18.5/xlsx.full.min.js"></script>
            <script>
              const duplicateData = ${JSON.stringify(data.duplicates.data)};

              function handleDownloadExcel() {
                if (!duplicateData || duplicateData.length === 0) {
                  console.error("No duplicate data available to export.");
                  return;
                }

                const sheet = XLSX.utils.json_to_sheet(duplicateData);
                const workbook = XLSX.utils.book_new();

                XLSX.utils.book_append_sheet(workbook, sheet, "Duplicate Data");

                const excelFile = XLSX.write(workbook, {
                  bookType: 'xlsx',
                  type: 'array'
                });

                const blob = new Blob([excelFile], {
                  type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
                });

                const url = URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.download = 'duplicate_student_data.xlsx';
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
              }
            </script>
          `);

          newWindow.document.write("</body></html>");
          setTimeout(() => {
            handleModalOpen(
              `There are ${data.duplicates.count} duplicates. Proceed with data insertion, skipping duplicates and inserting only unique entries?`,
            );
          }, 1000);
        } else {
          await insertData();
        }
      } else {
        setError(data.message || "Error verifying file. Please try again.");
      }
    } catch (error) {
      setError("Server error. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = () => {
    setSubmitted(true);

    const firstInvalid = FIELD_ORDER.find((key) => fieldErrors[key]);
    if (firstInvalid) {
      const count = Object.keys(fieldErrors).length;
      setError(
        `${count} required field${count > 1 ? "s are" : " is"} missing. Please fill the highlighted fields.`,
      );
      document.getElementById(firstInvalid)?.focus();
      return;
    }

    if (!file) {
      setError("");
      fileInputRef.current?.focus();
      return;
    }

    if (fileRowCount !== Number(recordCount)) {
      setError(
        `The file has ${fileRowCount} records but you entered ${recordCount}.`,
      );
      return;
    }

    setError("");
    setConfirmOpen(true);
  };

  const handleConfirmUpload = () => {
    setConfirmOpen(false);
    startUpload();
  };

  const insertData = async () => {
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append("excel_file", file);

      const response = await fetch(SummaryApi.excelUpload.url, {
        method: SummaryApi.excelUpload.method,
        headers: authHeaders,
        body: formData,
      });

      const data = await response.json();
      if (!data.status) {
        toast.error(data.message || "Error inserting data.");
        return;
      }

      const uploadedCount = Number(recordCount);
      const parseInsertedFromMessage = (msg) => {
        const match = String(msg || "").match(/(\d+)\s*record/i);
        return match ? Number(match[1]) : null;
      };
      const insertedCount = Number(
        data.inserted_count ??
        data.inserted ??
        data.data?.inserted_count ??
        parseInsertedFromMessage(data.message) ??
        0,
      );
      if (insertedCount <= 0) {
        toast.info(
          data.message || "No new records were inserted (all were duplicates).",
        );
        setIsInserted(true);
        resetForm();
        setTimeout(() => setIsInserted(false), 1000);
        return;
      }

      let detailsSaved = false;
      try {
        const detailsRes = await fetch(SummaryApi.createUpdataDetails.url, {
          method: SummaryApi.createUpdataDetails.method,
          headers: { "Content-Type": "application/json", ...authHeaders },
          body: JSON.stringify({
            region: region.trim(),
            district_id: districtId,
            description: description.trim(),
            uploaded_count: uploadedCount,
            inserted_count: insertedCount,
            uploaded_by: uploadedBy.trim(),
          }),
        });
        const detailsData = await detailsRes.json();
        detailsSaved = detailsRes.ok && detailsData.status !== false;
      } catch (err) {
        detailsSaved = false;
      }

      if (detailsSaved) {
        toast.success(data.message || "Data uploaded successfully");
      } else {
        toast.warn(
          "Students were inserted, but saving the upload details failed.",
        );
      }

      setIsInserted(true);
      resetForm();
      setTimeout(() => {
        setIsInserted(false);
      }, 1000);
    } catch (error) {
      toast.error("Error inserting data.");
    } finally {
      setLoading(false);
    }
  };

  const selectedDistrict = districts.find(
    (d) => String(d.district_id) === districtId,
  )?.district_name;

  // ---------- styling helpers ----------
  const inputBase =
    "block w-full p-2 border rounded-lg shadow-sm focus:ring-1 text-sm";

  const inputClass = (key) =>
    `${inputBase} ${submitted && fieldErrors[key]
      ? "border-red-500 bg-red-50 focus:ring-red-500 focus:border-red-500 cursor-pointer"
      : "border-gray-300 focus:ring-blue-500 focus:border-blue-500 cursor-pointer"
    }`;

  const showError = (key) => (submitted ? fieldErrors[key] : "");

  const doUpload = async () => {
    setConfirmOpen(false);
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("excel_file", file);

      const res = await axios({ ...SummaryApi.excelUpload, data: formData });
      const r = res.data;

      if (!r.status) {
        toast.error(r.message || "Upload failed");
        return;
      }

      const insertedCount = Number(r.inserted_count ?? 0);
      const skippedCount = Number(r.skipped_count ?? 0);
      const duplicates = r.duplicates?.data ?? [];

      setUploadResult({ insertedCount, skippedCount, duplicates });
      setActiveTab(insertedCount === 0 && skippedCount > 0 ? "duplicates" : "summary");

      if (insertedCount > 0) {
        try {
          const d = await axios({
            ...SummaryApi.createUpdataDetails,
            data: {
              region: form.region,
              district_id: form.district_id,
              description: form.description,
              uploaded_count: form.uploaded_count,
              inserted_count: insertedCount,
              uploaded_by: form.uploaded_by,
            },
          });
          if (!d.data?.status) {
            toast.warning(d.data?.message || "Upload saved, but details were not recorded");
          }
        } catch (err) {
          toast.warning("Data inserted, but saving upload details failed");
        }
        toast.success(`${insertedCount} record(s) uploaded`);
      } else if (skippedCount > 0) {
        toast.info(`All ${skippedCount} records already exist (duplicates)`);
      } else {
        toast.info("No records were inserted");
      }

      if (skippedCount > 0 && insertedCount > 0) {
        toast.info(`${skippedCount} duplicate record(s) skipped`);
      }

      setFile(null);
      setFileKey((k) => k + 1);
    } catch (err) {
      toast.error(err?.response?.data?.message || "Server error during upload");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="min-h-screen ">
      <div className=" space-y-6">

        {/* Header card — uses full width */}
        <div className="bg-white rounded-xl border border-slate-200 px-6 py-5 shadow-sm">
          <h1
            className="text-xl md:text-2xl font-bold tracking-tight"
            style={{ color: THEME.primary }}
          >
            Upload Excel File
          </h1>
          <p className="mt-1 text-sm text-gray-500">
            Fields marked <span className="text-red-500">*</span> are required.
          </p>
        </div>

        {/* Duplicate confirmation modal (narrow — it's a focused dialog) */}
        <Modal
          isOpen={modalIsOpen}
          onRequestClose={handleCancel}
          ariaHideApp={false}
          contentLabel="Duplicate Data Confirmation"
          className="w-full max-w-md mx-auto my-10 mt-[100px] bg-white rounded-lg shadow-lg overflow-hidden outline-none"
          overlayClassName="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50"
        >
          <div className="px-5 py-4 bg-white border-b border-slate-100">
            <h2
              className="text-sm font-bold tracking-wide"
              style={{ color: THEME.primary }}
            >
              Confirmation
            </h2>
          </div>
          <div className="p-5">
            <h2 className="text-base text-slate-800 mb-5">{confirmationMessage}</h2>
            <div className="flex justify-end gap-3">
              <button
                onClick={handleCancel}
                className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleProceed}
                style={{ backgroundColor: THEME.primary }}
                onMouseEnter={(e) => (e.currentTarget.style.opacity = '0.9')}
                onMouseLeave={(e) => (e.currentTarget.style.opacity = '1')}
                className="text-white py-2 px-4 rounded-lg transition-opacity"
              >
                Proceed
              </button>
            </div>
          </div>
        </Modal>

        {/* Form card — uses full width with responsive grid */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm">
          <div className="px-6 py-4 border-b border-slate-100">
            <h2
              className="text-base font-bold tracking-wide"
              style={{ color: THEME.primary }}
            >
              Upload Details
            </h2>
          </div>

          <div className="p-6 space-y-5">
            {/* Responsive grid — 1 / 2 / 3 columns */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {/* Region */}
              <Field id="region" label="Region" required error={showError("region")}>
                <input
                  id="region"
                  type="text"
                  className={inputClass("region")}
                  placeholder="Enter region"
                  value={region}
                  onChange={(e) => {
                    setRegion(e.target.value);
                    setError("");
                  }}
                />
              </Field>

              {/* District */}
              <Field
                id="districtId"
                label="District"
                required
                error={showError("districtId")}
              >
                <select
                  id="districtId"
                  className={inputClass("districtId")}
                  value={districtId}
                  onChange={(e) => {
                    setDistrictId(e.target.value);
                    setError("");
                  }}
                >
                  <option value="">Select District</option>
                  {districts.map((d) => (
                    <option key={d.district_id} value={String(d.district_id)}>
                      {d.district_name}
                    </option>
                  ))}
                </select>
              </Field>

              {/* Record Count */}
              <Field
                id="recordCount"
                label="No. of Records"
                required
                error={showError("recordCount")}
              >
                <input
                  id="recordCount"
                  type="number"
                  min="1"
                  className={inputClass("recordCount")}
                  placeholder="Enter number of records"
                  value={recordCount}
                  onChange={(e) => {
                    setRecordCount(e.target.value);
                    setError("");
                  }}
                />
              </Field>

              {/* Uploaded By */}
              <Field
                id="uploadedBy"
                label="Uploaded By"
                required
                error={showError("uploadedBy")}
              >
                <input
                  id="uploadedBy"
                  type="text"
                  className={inputClass("uploadedBy")}
                  placeholder="Enter your name"
                  value={uploadedBy}
                  onChange={(e) => {
                    setUploadedBy(e.target.value);
                    setError("");
                  }}
                />
              </Field>

              {/* Description — spans 2 cols on lg, full on md */}
              <div className="lg:col-span-2 md:col-span-2">
                <Field
                  id="description"
                  label="Description"
                  required
                  error={showError("description")}
                >
                  <textarea
                    id="description"
                    rows={2}
                    className={inputClass("description")}
                    placeholder="Enter description"
                    value={description}
                    onChange={(e) => {
                      setDescription(e.target.value);
                      setError("");
                    }}
                  />
                </Field>
              </div>
            </div>

            {/* File upload — full width row */}
            <Field
              id="excelFile"
              label="Upload Excel File"
              required
              error={fileError}
            >
              <input
                id="excelFile"
                ref={fileInputRef}
                type="file"
                accept=".xlsx,.xls"
                disabled={!detailsFilled}
                className={`block w-full p-2 border rounded-lg disabled:bg-gray-100 disabled:cursor-not-allowed cursor-pointer text-sm ${fileError ? "border-red-500 bg-red-50" : "border-gray-300"
                  }`}
                onChange={handleFileChange}
              />
              {!detailsFilled && (
                <p className="mt-1 text-xs text-gray-500">
                  Fill all the required details above to enable file upload.
                </p>
              )}
            </Field>

            {fileRowCount !== null && (
              <div className="font-bold text-base">
                Records Count:
                <span className="text-green-700 ml-2">{fileRowCount}</span>
              </div>
            )}

            {error && (
              <div className="text-red-500 text-sm bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                {error}
              </div>
            )}

            <div className="pt-2">
              <button
                onClick={handleSubmit}
                style={{
                  backgroundColor: loading || isInserted ? "#9CA3AF" : THEME.primary,
                }}
                onMouseEnter={(e) => {
                  if (!loading && !isInserted) e.currentTarget.style.opacity = "0.9";
                }}
                onMouseLeave={(e) => (e.currentTarget.style.opacity = "1")}
                className={`px-6 py-2.5 rounded-lg text-white text-sm font-semibold transition-opacity ${loading || isInserted ? "cursor-not-allowed" : ""
                  }`}
                disabled={loading || isInserted}
              >
                {loading ? "Uploading..." : "Upload Data"}
              </button>
            </div>
          </div>
        </div>

        {/* Preview card — full width */}
        {fileData && fileData.length > 0 && (
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm">
            <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
              <h3
                className="text-base font-bold"
                style={{ color: THEME.primary }}
              >
                Preview
              </h3>
              <span className="text-xs text-gray-500">
                Showing first 5 of {fileData.length} records
              </span>
            </div>
            <div className="p-6">
              <div className="overflow-x-auto rounded-lg border border-gray-200">
                <table className="min-w-full table-auto">
                  <thead className="bg-gray-200">
                    <tr>
                      {Object.keys(fileData[0]).map((key) => (
                        <th
                          key={key}
                          className="px-4 py-2 text-left text-sm font-medium whitespace-nowrap"
                        >
                          {key}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {fileData.slice(0, 5).map((row, index) => (
                      <tr key={index} className="hover:bg-gray-50">
                        {Object.keys(row).map((key) => (
                          <td
                            key={key}
                            className="px-4 py-2 text-sm text-gray-700 whitespace-nowrap"
                          >
                            {row[key]}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Confirm Upload modal (narrow — focused dialog) */}
      <Modal
        isOpen={confirmOpen}
        onRequestClose={() => setConfirmOpen(false)}
        ariaHideApp={false}
        contentLabel="Confirm Upload"
        className="w-full max-w-md mx-auto my-10 mt-[100px] bg-white rounded-lg shadow-lg overflow-hidden outline-none"
        overlayClassName="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50"
      >
        <div className="px-5 py-4 bg-white border-b border-slate-100">
          <h2
            className="text-sm font-bold tracking-wide"
            style={{ color: THEME.primary }}
          >
            Confirm Upload
          </h2>
        </div>
        <div className="p-5">
          <p className="text-sm text-gray-500 mb-4">
            Please check these details before uploading.
          </p>

          <dl className="text-sm divide-y divide-gray-100 mb-6">
            {[
              ["Region", region.trim()],
              ["District", selectedDistrict],
              ["Description", description.trim()],
              ["Records in file", fileRowCount],
              ["Uploaded by", uploadedBy.trim()],
              ["File", file?.name],
            ].map(([label, value]) => (
              <div key={label} className="flex justify-between gap-4 py-2">
                <dt className="text-gray-500">{label}</dt>
                <dd className="text-gray-900 font-medium text-right break-words">
                  {value ?? "-"}
                </dd>
              </div>
            ))}
          </dl>

          <div className="flex justify-end gap-3">
            <button
              onClick={() => setConfirmOpen(false)}
              className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50"
            >
              Go back
            </button>
            <button
              onClick={handleConfirmUpload}
              style={{ backgroundColor: THEME.primary }}
              onMouseEnter={(e) => (e.currentTarget.style.opacity = "0.9")}
              onMouseLeave={(e) => (e.currentTarget.style.opacity = "1")}
              className="px-4 py-2 rounded-lg text-white transition-opacity"
            >
              Confirm and upload
            </button>
          </div>
        </div>
      </Modal>

      {/* RESULT TABS — full width */}
      {uploadResult && (
        <div className=" mt-6 bg-white rounded-xl border border-slate-200 shadow-sm">
          <div className="flex border-b border-slate-100">
            <button
              onClick={() => setActiveTab("summary")}
              className={`px-6 py-3 text-sm font-semibold ${activeTab === "summary" ? "border-b-2" : "text-gray-500"
                }`}
              style={
                activeTab === "summary"
                  ? { borderColor: THEME.primary, color: THEME.primary }
                  : {}
              }
            >
              Upload Summary
            </button>
            <button
              onClick={() => setActiveTab("duplicates")}
              className={`px-6 py-3 text-sm font-semibold ${activeTab === "duplicates" ? "border-b-2" : "text-gray-500"
                }`}
              style={
                activeTab === "duplicates"
                  ? { borderColor: THEME.actionRed, color: THEME.actionRed }
                  : {}
              }
            >
              Duplicates ({uploadResult.skippedCount})
            </button>
          </div>

          <div className="p-6">
            {activeTab === "summary" && (
              <div className="space-y-2 text-sm">
                <p>
                  Inserted:{" "}
                  <b className="text-green-600">{uploadResult.insertedCount}</b>
                </p>
                <p>
                  Skipped (duplicates):{" "}
                  <b className="text-red-600">{uploadResult.skippedCount}</b>
                </p>
              </div>
            )}

            {activeTab === "duplicates" &&
              (uploadResult.duplicates.length === 0 ? (
                <p className="text-sm text-gray-500">No duplicate records.</p>
              ) : (
                <>
                  <div className="flex justify-end mb-3">
                    <button
                      onClick={downloadDuplicates}
                      style={{ backgroundColor: THEME.actionRed }}
                      onMouseEnter={(e) =>
                        (e.currentTarget.style.backgroundColor = THEME.actionRedHover)
                      }
                      onMouseLeave={(e) =>
                        (e.currentTarget.style.backgroundColor = THEME.actionRed)
                      }
                      className="px-4 py-2 text-sm text-white rounded-lg transition-colors shadow-sm"
                    >
                      Download Duplicates
                    </button>
                  </div>
                  <div className="overflow-x-auto max-h-96 overflow-y-auto rounded-lg border border-gray-200">
                    <table className="min-w-full text-sm">
                      <thead className="bg-gray-100 sticky top-0 z-10">
                        <tr>
                          {[
                            "#",
                            "Mobile",
                            "Student Name",
                            "College",
                            "Course",
                            "Year",
                            "District",
                            "City",
                          ].map((h) => (
                            <th
                              key={h}
                              className="px-4 py-3 text-left font-semibold whitespace-nowrap"
                              style={{ color: THEME.primary }}
                            >
                              {h}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {uploadResult.duplicates.map((d, i) => (
                          <tr key={i} className="hover:bg-gray-50">
                            <td className="px-4 py-2">{i + 1}</td>
                            <td className="px-4 py-2">{d.mobile_number}</td>
                            <td className="px-4 py-2">{d.student_name}</td>
                            <td className="px-4 py-2">{d.clg_name}</td>
                            <td className="px-4 py-2">{d.course}</td>
                            <td className="px-4 py-2">{d.year}</td>
                            <td className="px-4 py-2">{d.district}</td>
                            <td className="px-4 py-2">{d.city}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default ExcelUploadPage;